import type { ContentTemplateInstanceNode } from "building-generation/contentTemplates/planSetsEvaluate";
import type { Annotation } from "building-types/annotation/annotationTypes";
import type { Building } from "building-types/building/buildingTypes";
import type { Asset } from "building-types/entities/asset";
import type { LabelableEntityBase } from "building-types/entities/labelTypes";
import type { SheetGeometryCategory } from "building-types/views/sheetGeometryCategory";
import type { SheetGeometry } from "building-types/views/sheetGeometryTypes";
import type { Table } from "common/data/table";
import { makeSheetGeometryFromAnnotations } from "doc/plans/planAnnotations";
import { sheetGeometryYFlipTransform, sheetGeometryTransform } from "doc/plans/sheetGeometryTools";
import { labelFilterPredicate } from "doc/utils/labelTools";
import { makeGeometrySheetNode } from "lib/house/plans/nodes/geometrySheetNode";
import type { SheetNode } from "lib/house/plans/sheetNode";

export function contentTemplateInstanceNodeAnnotationToSheetNodes(
  node: ContentTemplateInstanceNode,
  assetCatalog: Table<Asset>,
  labelFilterEntity: LabelableEntityBase | undefined,
  building?: Building,
  selectorInstance: ContentTemplateInstanceNode | undefined = undefined,
): SheetNode[] {
  const children: SheetGeometry[] = [];

  children.push(
    ...contentTemplateInstanceNodeAnnotationToSheetGeometryInternal(
      node.instanceChildren.annotation,
      assetCatalog,
      labelFilterEntity,
      building,
      undefined,
      true,
    ),
  );

  children.push(
    ...contentTemplateInstanceNodeAnnotationToSheetGeometryInternal(
      node.templateChildren.annotation,
      assetCatalog,
      labelFilterEntity,
      building,
      selectorInstance,
      true,
    ),
  );

  return children.map((x) => makeGeometrySheetNode(x));
}

// In practice this is only used for drawing templates in drawing viewports.
// This doesn't flip y.
export function contentTemplateInstanceNodeAnnotationToSheetGeometry(
  node: ContentTemplateInstanceNode,
  assetCatalog: Table<Asset>,
  building?: Building,
  selectorInstance?: ContentTemplateInstanceNode,
) {
  const children: SheetGeometry[] = [];

  children.push(
    ...contentTemplateInstanceNodeAnnotationToSheetGeometryInternal(
      node.instanceChildren.annotation,
      assetCatalog,
      undefined,
      building,
    ),
  );

  children.push(
    ...contentTemplateInstanceNodeAnnotationToSheetGeometryInternal(
      node.templateChildren.annotation,
      assetCatalog,
      undefined,
      building,
      selectorInstance,
      false,
    ),
  );

  return children;
}

function contentTemplateInstanceNodeAnnotationToSheetGeometryInternal(
  annotationTable: Table<Annotation>,
  assetCatalog: Table<Asset>,
  labelFilterEntity: LabelableEntityBase | undefined,
  building?: Building,
  selectorInstance: ContentTemplateInstanceNode | undefined = undefined,
  flipY: boolean = true,
): SheetGeometry[] {
  // Sort in descending order - annotations at the top of the list get rendered last.
  let annotation = Object.values(annotationTable);

  if (labelFilterEntity) {
    annotation = annotation.filter((x) => labelFilterPredicate(labelFilterEntity, x));
  }

  annotation = annotation.sort((a, b) => b.sortIndex - a.sortIndex);

  if (!selectorInstance) {
    // implies the annotations are individually selectable
    annotation = annotation.map((x) => ({
      ...x,
      handle: { type: "ANNOTATION", id: x.id },
    })) as Annotation[];
  }

  if (!annotation.length) {
    return [];
  }

  /**
   * In order to convert annotation into sheet layout entities,
   * we first extract them as plan geometry, the same way we do for plan
   * sets. These plan geometries will be scaled to the page size of the content template.
   *
   * If the output of the content template needs to be at a different scale (for example export to PDF has a PDF_DPI multiplier),
   * the retulting planGeometries are scaled by that factor before being converted to sheet layout entities
   */

  const annotationPlanGeometry = makeSheetGeometryFromAnnotations({
    building,
    annotationOptions: {
      // the leader arrows for plan sets are scaled in world inches, rather
      // than paper inches. This will override the leader arrow size.
      // TODO - arrow size may be better off as a property of the note
      annotationNoteLeaderArrowProps: {
        width: 0.1,
        length: 0.2,
      },
    },
    annotation,
    assetCatalog,
  });

  const yFlipTransform = sheetGeometryYFlipTransform();

  const children: SheetGeometry[] = [];

  for (const category in annotationPlanGeometry.taggedGeometries) {
    const annotGeom = annotationPlanGeometry.taggedGeometries[category] as SheetGeometry[];
    for (const geom of annotGeom) {
      let geometry = geom;

      if (flipY) {
        geometry = sheetGeometryTransform(geom, yFlipTransform); // TODO: Remove y flip here
      }

      if (selectorInstance) {
        geometry = {
          ...geometry,
          handle: { type: "CONTENT_TEMPLATE_INSTANCE", id: selectorInstance.instance.id },
        };
      }

      // TODO https://linear.app/higharc/issue/SDO-1646/add-sheetgeometrycategory-field-to-sheetgeometry
      // Once we finish this, it will be unecessary to do this
      children.push({ ...geometry, category: category as SheetGeometryCategory });
    }
  }

  return children.sort((a, b) => (a.renderOrder ?? 0) - (b.renderOrder ?? 0));
}
